import { html, LitElement, PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { isElementOverflowed, OverflowDirection } from '../../../utils/htmlElement.utils';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzTooltip } from './GzTooltip';
import { TooltipSeverity } from '../types';
import {
  addFullscreenEventListeners,
  removeFullscreenEventListeners,
  isBrowserFullscreen,
} from '../../../utils/fullscreen.utils';
import { GzTooltipBoxWebComponent } from './GzTooltipBoxWebComponent';
import { WEB_COMPONENT_GZ_TOOLTIP_BOX } from '../../../webcomponents.constants';

export class GzTooltipWebComponent extends LitElement {
  @property({
    type: String,
    reflect: true,
  })
  tooltipText = '';

  @property({
    type: String,
    reflect: true,
  })
  tooltipClass: TooltipSeverity = TooltipSeverity.NEUTRAL;

  private boxIsVisible = false;

  /**
   * If this property is set to true:
   * 1. show an ellipsis if the text is longer than the container
   * 2. display the tooltip only if the content overflows the parent
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  isEllipsis = false;

  @state()
  private isFullscreen = false;

  private fullscreenChangeListener = this.onFullscreenChange.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.handleTooltipShow);
    this.addEventListener('mousemove', GzTooltip.align);
    this.addEventListener('mouseleave', this.hideTooltip);
    addFullscreenEventListeners(window, this.fullscreenChangeListener);
  }

  updated(_changedProperties: PropertyValues) {
    if (!this.boxIsVisible) {
      return;
    }
    const tooltipBox = window.document.getElementsByTagName(
      WEB_COMPONENT_GZ_TOOLTIP_BOX
    )[0] as GzTooltipBoxWebComponent;
    if (tooltipBox) {
      tooltipBox.text = this.tooltipText;
    }
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.handleTooltipShow);
    this.removeEventListener('mousemove', GzTooltip.align);
    this.removeEventListener('mouseleave', this.hideTooltip);
    removeFullscreenEventListeners(window, this.fullscreenChangeListener);
    super.disconnectedCallback();
  }

  private onFullscreenChange() {
    this.isFullscreen = isBrowserFullscreen();
  }

  static get styles() {
    return styles;
  }

  render() {
    if (this.isFullscreen) {
      return html`<slot title="${this.tooltipText}"></slot>`;
    }
    return html`<slot></slot>`;
  }

  handleTooltipShow(event: MouseEvent) {
    if (isEmpty(this.tooltipText)) {
      return;
    }
    // isEllipsis === FALSE => will always show the tooltip
    if (!this.isEllipsis) {
      this.showTooltip(event);
    } else {
      // isEllipsis === TRUE => will show the tooltip only if the trigger has overflowing content
      if (
        !isElementOverflowed(this, OverflowDirection.HORIZONTAL) &&
        !isElementOverflowed(this, OverflowDirection.VERTICAL)
      ) {
        return;
      } else {
        this.showTooltip(event);
      }
    }
  }

  hideTooltip() {
    GzTooltip.hide();
    this.boxIsVisible = false;
  }

  showTooltip(event: MouseEvent) {
    GzTooltip.show(this.tooltipText, event, this.tooltipClass);
    this.boxIsVisible = true;
    GzTooltip.align(event);
  }
}
